import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import { pickDefaultOrg } from '@/store/defaults';

type GroupBy = 'nomenclature' | 'contractor' | 'nomenclature_group' | 'created_by';

const GROUP_LABELS: Record<GroupBy, string> = {
  nomenclature: 'За товарами',
  contractor: 'За клієнтами',
  nomenclature_group: 'За групами товарів',
  created_by: 'За менеджерами',
};

interface ProfitLine {
  key_id: string | null;
  code: string | null;
  name: string;
  revenue: number;
  cogs: number;
  margin: number;
  margin_pct: number;
  quantity: number;
  deals: number;
}

interface ProfitReport {
  period_start: string;
  period_end: string;
  group_by: GroupBy;
  lines: ProfitLine[];
  totals: { revenue: number; cogs: number; margin: number; margin_pct: number; quantity: number; deals: number };
}

const fmt = (v: number) => v.toLocaleString('uk-UA', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

export default function ProfitabilityReportPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('nomenclature');
  const today = new Date();
  const [periodStart, setPeriodStart] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10),
  );
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().slice(0, 10));

  useEffect(() => {
    if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);
  }, [orgList.data, orgId]);

  const reportQ = useQuery({
    queryKey: ['profitability', orgId, periodStart, periodEnd, groupBy],
    queryFn: () => api.get<ProfitReport>('/profitability', {
      params: {
        organization_id: orgId,
        period_start: periodStart,
        period_end: periodEnd,
        group_by: groupBy,
      },
    }).then((r) => r.data),
    enabled: !!orgId,
  });

  const data = reportQ.data;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Звіт прибутковості</h2>
        <button onClick={() => window.print()}>🖨 Друкувати</button>
      </div>

      <div className="doc-grid" style={{ marginBottom: '1rem' }}>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Розріз</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
            {(Object.keys(GROUP_LABELS) as GroupBy[]).map((k) =>
              <option key={k} value={k}>{GROUP_LABELS[k]}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) =>
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період з</label>
          <DatePopover value={periodStart} onChange={(v) => setPeriodStart(v)} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Період по</label>
          <DatePopover value={periodEnd} onChange={(v) => setPeriodEnd(v)} />
        </div>
      </div>

      {data && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem',
            marginBottom: '1rem', padding: '0.75rem',
            background: 'var(--primary-soft)', borderRadius: 'var(--radius)',
          }}>
            <KPI label="Виручка" value={fmt(data.totals.revenue)} />
            <KPI label="Собівартість" value={fmt(data.totals.cogs)} />
            <KPI label="Маржа" value={fmt(data.totals.margin)} highlight />
            <KPI label="Маржа %"
                 value={`${data.totals.margin_pct.toFixed(1)}%`}
                 highlight />
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem', width: 110 }}>Код</th>
                <th style={{ padding: '0.5rem' }}>{GROUP_LABELS[groupBy]}</th>
                <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Виручка</th>
                <th style={{ padding: '0.5rem', width: 130, textAlign: 'right' }}>Собівартість</th>
                <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>Маржа</th>
                <th style={{ padding: '0.5rem', width: 80, textAlign: 'right' }}>%</th>
                <th style={{ padding: '0.5rem', width: 80, textAlign: 'right' }}>Док.</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>
                    Немає продажів за період.
                  </td>
                </tr>
              ) : (
                data.lines.map((l, i) => {
                  const color = l.margin_pct < 10 ? 'var(--danger)' :
                                 l.margin_pct < 25 ? 'var(--warning)' :
                                 'var(--success)';
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-soft)' }}>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>
                        {l.code ?? '—'}
                      </td>
                      <td style={{ padding: '0.5rem' }}>{l.name}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right',
                                    fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(l.revenue)}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right',
                                    fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(l.cogs)}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right',
                                    color, fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(l.margin)}
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color, fontWeight: 600 }}>
                        {l.margin_pct.toFixed(1)}%
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--muted)' }}>
                        {l.deals}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
            ⓘ Виручка — без ПДВ, з проведених документів реалізації.
            Собівартість — фактичні списання FIFO з реєстра партій.
            Послуги без собівартості показуються з 100% маржею.
          </p>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{
        fontSize: '1.4rem', fontWeight: 700, marginTop: '0.2rem',
        color: highlight ? 'var(--primary)' : 'var(--text)',
      }}>
        {value}
      </div>
    </div>
  );
}
